import loadable from '@loadable/component';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';

import BpkMobileScrollContainer from '@skyscanner/backpack-web/bpk-component-mobile-scroll-container';
import BpkSectionHeader from '@skyscanner/backpack-web/bpk-component-section-header';

import logMiniEventHelper from '../../../mini-event/logMiniEventHelper';
import DesktopScrollContainer from '../../common/DesktopScrollContainer/DesktopScrollContainer';
import { useMBDContext } from '../../common/MBD/useMBDContext';
import RatingsCard from '../RatingsCard/RatingsCard';

import type {
  ProviderRatingProps,
  ProviderInfo,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ProviderRating';

import STYLES from './ProviderList.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const ProviderList = (props: ProviderRatingProps) => {
  const {
    enableDesktopScroll,
    headerText,
    providerInfoList,
    strings,
    subHeaderText,
  } = props;
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!providerInfoList?.length) {
    return null;
  }

  return (
    <div
      className={STYLES.ProviderList}
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      {headerText && (
        <div
          className={`${STYLES.ProviderList__header} ${
            enableDesktopScroll
              ? STYLES.ProviderList__header__remainScrolllerSpace
              : ''
          }`}
        >
          <BpkSectionHeader title={headerText} description={subHeaderText} />
        </div>
      )}
      {enableDesktopScroll ? (
        <DesktopScrollContainer nextLabel="next" prevLabel="prev">
          {providerInfoList.map((provider: ProviderInfo, index: number) => (
            <RatingsCard
              key={`${provider.providerName}-desktop`}
              strings={strings}
              provider={provider}
              index={index}
            />
          ))}
        </DesktopScrollContainer>
      ) : (
        <div className={STYLES.ProviderList__desktop}>
          {providerInfoList.map((provider: ProviderInfo, index: number) => (
            <div
              key={`${provider.providerName}-desktop`}
              className={STYLES.ProviderList__desktop__cardContainer}
            >
              <RatingsCard
                strings={strings}
                provider={provider}
                index={index}
              />
            </div>
          ))}
        </div>
      )}

      <BpkMobileScrollContainer
        className={STYLES.ProviderList__mobileScroll}
        trailingIndicatorClassName={STYLES.Indicator}
      >
        <div className={STYLES.ProviderList__list}>
          {providerInfoList.map((provider: ProviderInfo, index: number) => (
            <div
              key={provider.providerName}
              className={STYLES.ProviderList__container}
            >
              <RatingsCard
                strings={strings}
                provider={provider}
                index={index}
              />
            </div>
          ))}
        </div>
      </BpkMobileScrollContainer>
    </div>
  );
};

export default (props: ProviderRatingProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.PROVIDER_RATING,
      })
    }
  >
    <ProviderList {...props} />
  </IntersectionObserverWrapper>
);
